/**
 * This slice is dedicated to application state data
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as base64 from '../../../util/base64';

import { CallStatus } from '../../enums';
import { IGNORE_GATEWAY_CONNECTION } from '../../libSettings';
import { LoggerService } from '../../services/logger/Logger.service';
import {
  accountsFailed,
  applicationStartup, ErrorPayloadFull,
  gatewayConnected,
  gatewayDisconnected,
  login,
  logout,
  marketDataDisconnected,
  servicesReady,
  tradingDisconnected,
} from '../common-actions';

import { DEFAULT_USER_PREFERENCE_DATA } from './constants';
import { setAppCallStatus } from './helpers';
import { AppCall, AppState, UserPreferencesData } from './types';

export const INITIAL_STATE: AppState = {
  connected: false,
  loaded: false,
  isServicesReady: false,
  isWebLoginReady: false,
  isOnline: true,
  isConnectionSlow: false,
  isDemoExpirationModalDisplayed: false,
  userPreferencesData: DEFAULT_USER_PREFERENCE_DATA,
  statusByCall: {},
};

const logger = LoggerService.getLogger();
const appSlice = createSlice({
  name: 'app',
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * All services connect - action called in epic when all services connected (crm, trading, md)
     */
    appConnected(state) {
      state.connected = true;
      state.loaded = true;
      state.isOnline = true;
    },
    /**
     * App has lodaded - called only internally - see crm/epics@startupEpic
     */
    appLoaded(state) {
      state.loaded = true;
    },
    /**
     * This is called only once by Web UI - when authentication is done
     * ATTENTION: Do not use for other purposes
     */
    setWebLoginReady(state) {
      state.isWebLoginReady = true;
    },
    setError(state, action: PayloadAction<boolean>) {
    },
    setOnlineStatus(state, action: PayloadAction<boolean>) {
      state.isOnline = action.payload;
    },
    setSlowConnectionStatus(state, action: PayloadAction<boolean>) {
      state.isConnectionSlow = action.payload;
    },
    setDemoExpirationModalDisplayed(state, action: PayloadAction<boolean>) {
      state.isDemoExpirationModalDisplayed = action.payload;
    },
    uploadUserPreferencesData(state, action) {
      setAppCallStatus(AppCall.uploadUserPreferencesData, CallStatus.PENDING, state);
    },
    uploadUserPreferencesDataCompleted(state, action) {
      const { customData } = action.payload;
      const { request } = customData;
      state.userPreferencesData = request;
      setAppCallStatus(AppCall.uploadUserPreferencesData, CallStatus.READY, state);
    },
    uploadUserPreferencesDataFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setAppCallStatus(AppCall.uploadUserPreferencesData, CallStatus.ERROR, state, status);
    },
    getUserPreferencesData(state) {
      setAppCallStatus(AppCall.getUserPreferencesData, CallStatus.PENDING, state);
    },
    getUserPreferencesDataCompleted(state, action) {
      try {
        const data = JSON.parse(base64.decode(action.payload.data)) as UserPreferencesData;
        const isUserFirstLogin = action.payload.customData?.isUserFirstLogin;
        state.userPreferencesData = { ...data, hasSeenWelcomePopup: data.hasSeenWelcomePopup || !isUserFirstLogin };
        setAppCallStatus(AppCall.getUserPreferencesData, CallStatus.READY, state);
      } catch (error) {
        logger.warn('can not parse response to json! error:', error);
        state.userPreferencesData = DEFAULT_USER_PREFERENCE_DATA;
        setAppCallStatus(AppCall.getUserPreferencesData, CallStatus.ERROR, state);
      }
    },
    getUserPreferencesDataFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      if (status === 404) {
        const isUserFirstLogin = action.payload.customData?.isUserFirstLogin;
        state.userPreferencesData = {
          ...DEFAULT_USER_PREFERENCE_DATA,
          hasSeenWelcomePopup: !isUserFirstLogin,
        };
      }
      setAppCallStatus(AppCall.getUserPreferencesData, CallStatus.ERROR, state, status);
    },
  },
  extraReducers: {
    [applicationStartup.type]: state => {
      state.connected = false;
      state.loaded = false;
      state.isServicesReady = false;
    },
    [login.type]: state => {
    },
    [servicesReady.type]: state => {
      state.isServicesReady = true;
    },
    [logout.type]: () => ({ ...INITIAL_STATE }),
    [tradingDisconnected.type]: state => {
      // TODO: Figure out app states on different connection issue cases
      //       Currently disabling this - until SOR integration
      // state.connected = false;
      // state.loaded = true;
    },
    [marketDataDisconnected.type]: state => {
      state.connected = false;
      state.loaded = true;
    },
    [gatewayConnected.type]: state => { },
    [gatewayDisconnected.type]: state => {
      if (!IGNORE_GATEWAY_CONNECTION) state.connected = false;
    },
    [accountsFailed.type]: state => {
      state.connected = false;
      state.loaded = true;
    },
  },
});

export const {
  setWebLoginReady,
  setOnlineStatus,
  setSlowConnectionStatus,
  setError,
  setDemoExpirationModalDisplayed,
  appConnected,
  appLoaded,
  uploadUserPreferencesData,
  uploadUserPreferencesDataCompleted,
  uploadUserPreferencesDataFailed,
  getUserPreferencesData,
  getUserPreferencesDataCompleted,
  getUserPreferencesDataFailed,
} = appSlice.actions;

export default appSlice.reducer;

export type { AppState } from './types';
